import useSWR from "swr";
import axios, { fetcher } from "src/utils/axios";
import type {
  AssociationCapabilities,
  AssociationList,
  AssociationLink,
  AssociationMyFees,
  AssociationCheckout,
} from "src/types/association";

export const associationBase = (id: number) => `/providers/${id}/association`;
export function useAssociationCapabilities(id: number, enabled = true) {
  return useSWR<AssociationCapabilities>(
    id > 0 && enabled ? associationBase(id) : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export function useAssociationList<T>(
  id: number,
  kind: string,
  enabled: boolean,
  offset = 0,
  search = "",
) {
  return useSWR<AssociationList<T>>(
    id > 0 && enabled
      ? [
          `${associationBase(id)}/${kind}`,
          { params: { offset, limit: 20, search } },
        ]
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}
export function useAssociationDetail<T>(
  id: number,
  kind: string,
  entityId: number | null,
  enabled: boolean,
) {
  return useSWR<T>(
    id > 0 && entityId && enabled
      ? `${associationBase(id)}/${kind}/${entityId}`
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}
export function useAssociationMyFees(id: number) {
  return useSWR<AssociationMyFees>(
    id > 0 ? `${associationBase(id)}/my-fees` : null,
    fetcher,
    { keepPreviousData: false },
  );
}
export function useAssociationLinks() {
  return useSWR<AssociationLink[]>(
    "/providers/association/account-links",
    fetcher,
    { keepPreviousData: false },
  );
}
export async function associationCommand<T>(
  id: number,
  path: string,
  payload: Record<string, unknown>,
) {
  const { data } = await axios.post<T>(
    `${associationBase(id)}/${path}`,
    payload,
  );
  return data;
}
export async function decideAssociationLink(
  id: number,
  decision: "accept" | "decline",
  key: string,
) {
  await axios.post(`/providers/association/account-links/${id}/decide`, {
    decision,
    idempotency_key: key,
  });
}
export async function associationCheckout(
  id: number,
  assessmentId: number | null,
  key: string,
  productId?: number,
) {
  return associationCommand<AssociationCheckout>(
    id,
    assessmentId ? `my-fees/${assessmentId}/checkout` : "my-fees/purchase",
    { idempotency_key: key, ...(productId ? { product_id: productId } : {}) },
  );
}

export function useAssociationWorkspaces() {
  return useSWR<{ id: number; name: string }[]>(
    "/providers/association/workspaces",
    fetcher,
    { keepPreviousData: false },
  );
}

export function useAssociationFeeOptions(
  id: number,
  enabled: boolean,
  memberSearch: string,
  productSearch: string,
) {
  return useSWR<{
    members: { id: number; label: string }[];
    products: { id: number; label: string }[];
  }>(
    id > 0 && enabled
      ? [
          `${associationBase(id)}/fee-options`,
          {
            params: {
              member_search: memberSearch,
              product_search: productSearch,
            },
          },
        ]
      : null,
    fetcher,
    { keepPreviousData: false },
  );
}

// Axios rejects response bodies in this application; support that shape and
// native Axios errors without exposing transport details to the UI.
export function associationErrorDetail(error: unknown): unknown {
  const value = error as {
    detail?: unknown;
    response?: { data?: { detail?: unknown } };
  } | null;
  return value?.detail ?? value?.response?.data?.detail;
}

export function associationReadErrorKey(error: unknown) {
  switch (associationErrorDetail(error)) {
    case "association_setup_required":
      return "association_setup_required";
    case "association_permission_denied":
      return "association_permission_error";
    case "association_not_found":
      return "association_workspace_unavailable";
    default:
      return "association_load_error";
  }
}
